<!--
/**
 * @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
 * @license AGPL-3.0
 *
 * This code is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.
 *
 * Any use of the work other than as authorized under this license or copyright law is prohibited.
 */
-->
<template>
  <span v-if="hasCertificate || status"
    :aria-label="i18n.verified"
    class="authenticity"
    data-test="dmark-bimi"
    ref="el"
  >
    <BaseIcon v-if="record?.certificateType === 'vmc'" icon-name="bi/patch-check-fill.svg" class="bi-14" style="color: var(--bimi-check)"></BaseIcon>
    <BaseIcon v-else-if="record?.certificateType === 'cmc'" icon-name="bi/patch-check.svg" class="bi-14" style="color: var(--text-gray)"></BaseIcon>
    <BaseIcon v-else-if="status" :icon-name="icon.name" :class="`bi-14 authenticity-icon-${status}`" :style="{ color: icon.color }"></BaseIcon>
  </span>
</template>

<script setup>
import $ from '@/jquery'
import BaseIcon from '@/io.ox/vueComponents/base-icon.vue'
import { useBimiStore } from '@/io.ox/core/bimi/store.js'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import gt from 'gettext'

const i18n = {
  // #. Verified = this email address (or domain) has been verified
  verified: gt('Verified'),
  // #. email sender verification (DMARC/BIMI)
  title: gt('Sender verification')
}

const { domain, status, message } = defineProps({
  domain: { type: String, default: '' },
  // status (based on DMARC): trusted, pass, neutral, fail, suspicious
  status: { type: String, default: '' },
  // optional: message for that status
  message: { type: String, default: '' }
})

const el = ref(null)
const store = useBimiStore()
const record = store.getRecord(domain)
const hasCertificate = computed(() => {
  return record.value.certificateType === 'vmc' || record.value.certificateType === 'cmc'
})
const icon = computed(() => {
  if (/(pass|trusted)/.test(status)) return { color: 'var(--dmarc-check)', name: 'bi/patch-check.svg' }
  if (/(fail|suspicious)/.test(status)) return { color: 'var(--attention)', name: 'bi/exclamation-triangle.svg' }
  return { color: 'var(--text)', name: 'bi/question-circle.svg' }
})

const popup = computed(() => {
  const publicSuffix = store.getPublicSuffix(domain)
  switch (record.value.certificateType) {
    case 'vmc': return { content: gt('The sender, the domain %1$s, and their official logo have been verified.', `<b>${publicSuffix.value}</b>`), html: true }
    case 'cmc': return { content: gt('The sender and the domain %1$s have been verified. The logo has been confirmed as provided by them.', `<b>${publicSuffix.value}</b>`), html: true }
    default: return { content: message, html: false }
  }
})

async function initPopover () {
  await nextTick()
  $(el.value).popover('destroy')
  $(el.value).popover({
    animation: false,
    container: 'body',
    placement: 'right',
    title: i18n.title,
    trigger: 'focus hover',
    ...popup.value
  })
}

onMounted(initPopover)
watch([popup], initPopover)

</script>
